/**
 * FSD Part 10 — Point of Sale. Sections follow the FSD's feature
 * specifications 10.2.1–10.2.9 (POS-F01…POS-F09) in order. 10.2.3 and 10.2.6
 * have no section: no screen's home is either. POS settings also implements
 * 10.2.6–10.2.8 and retail ledgers 10.2.10; those are `related` chips only.
 *
 * Item ids are stable and must never change: favourites and recents are
 * keyed off them in localStorage. Section ids (`s-<fsd>`) are new in C17.
 *
 * Moved in by C17: loyalty programmes (from Part 16) into POS-F05, and F&B
 * depletions / usage variance (from Part 7) into POS-F08 — each keeps its
 * original id and permission.
 *
 * Operator PINs sit with the outlet masters, not under devices: an operator
 * is a tenant user, and a PIN is set once for that person rather than per till.
 */
import type { NavGroup } from '../types'
import { DEVICE_PERMISSIONS, POS_PERMISSIONS } from '@/modules/pos/permissions'
import { INVENTORY_PERMISSIONS } from '@/modules/inventory/permissions'
import { PARTY_PERMISSIONS } from '@/modules/party/permissions'

export const P10_POS_GROUPS: NavGroup[] = [
  {
    id: 's-10.2.1',
    kind: 'feature',
    fsd: '10.2.1',
    code: 'POS-F01',
    labelKey: 'nav.fsd.s10_2_1.short',
    fullTitleKey: 'nav.fsd.s10_2_1.full',
    items: [
      {
        id: 'pos-outlets',
        labelKey: 'pos.routeTitles.outlets',
        to: '/pos/outlets',
        icon: 'building',
        permission: POS_PERMISSIONS.outletRead,
      },
      {
        id: 'pos-settings',
        labelKey: 'pos.routeTitles.settings',
        to: '/pos/settings',
        icon: 'settings',
        permission: POS_PERMISSIONS.outletRead,
        // Restores the references C15 carried in description copy (removed in C17).
        related: ['10.2.6', '10.2.7', '10.2.8'],
      },
      {
        id: 'pos-operator-pins',
        labelKey: 'pos.routeTitles.operatorPins',
        to: '/pos/operator-pins',
        icon: 'key',
        permission: POS_PERMISSIONS.operatorManage,
      },
      {
        id: 'pos-sales',
        labelKey: 'pos.routeTitles.sales',
        to: '/pos/sales',
        icon: 'inbox',
        permission: POS_PERMISSIONS.saleRead,
      },
      {
        id: 'pos-anomalies',
        labelKey: 'pos.routeTitles.anomalies',
        to: '/pos/anomalies',
        icon: 'alert',
        permission: POS_PERMISSIONS.anomalyRead,
      },
    ],
  },
  {
    id: 's-10.2.2',
    kind: 'feature',
    fsd: '10.2.2',
    code: 'POS-F02',
    labelKey: 'nav.fsd.s10_2_2.short',
    fullTitleKey: 'nav.fsd.s10_2_2.full',
    items: [
      {
        id: 'pos-devices',
        labelKey: 'pos.routeTitles.devices',
        to: '/pos/devices',
        icon: 'monitor',
        permission: DEVICE_PERMISSIONS.deviceRead,
      },
      {
        id: 'pos-content-sets',
        labelKey: 'pos.routeTitles.contentSets',
        to: '/pos/content-sets',
        icon: 'download',
        permission: DEVICE_PERMISSIONS.contentRead,
      },
      {
        id: 'pos-sync-conflicts',
        labelKey: 'pos.routeTitles.conflicts',
        to: '/pos/sync-conflicts',
        icon: 'refresh',
        permission: DEVICE_PERMISSIONS.syncRead,
      },
      {
        id: 'pos-ratifications',
        labelKey: 'pos.routeTitles.ratifications',
        to: '/pos/ratifications',
        icon: 'check',
        permission: DEVICE_PERMISSIONS.syncRead,
      },
    ],
  },
  {
    id: 's-10.2.4',
    kind: 'feature',
    fsd: '10.2.4',
    code: 'POS-F04',
    labelKey: 'nav.fsd.s10_2_4.short',
    fullTitleKey: 'nav.fsd.s10_2_4.full',
    items: [
      {
        id: 'pos-availability',
        labelKey: 'pos.routeTitles.availability',
        to: '/pos/availability',
        icon: 'ban',
        permission: POS_PERMISSIONS.menuRead,
      },
      {
        id: 'pos-floor-plan',
        labelKey: 'pos.routeTitles.floorPlan',
        to: '/pos/floor-plan',
        icon: 'dashboard',
        permission: POS_PERMISSIONS.checkRead,
      },
    ],
  },
  {
    id: 's-10.2.5',
    kind: 'feature',
    fsd: '10.2.5',
    code: 'POS-F05',
    labelKey: 'nav.fsd.s10_2_5.short',
    fullTitleKey: 'nav.fsd.s10_2_5.full',
    items: [
      {
        id: 'pos-menus',
        labelKey: 'pos.routeTitles.menus',
        to: '/pos/menus',
        icon: 'menu',
        permission: POS_PERMISSIONS.menuRead,
      },
      {
        id: 'pos-superseded-pricing',
        labelKey: 'pos.routeTitles.supersededPricing',
        to: '/pos/reports/superseded-pricing',
        icon: 'history',
        permission: POS_PERMISSIONS.saleRead,
      },
      // Moved in from Part 16 by C17. Loyalty is specified here, not as a
      // platform feature: POS-F05 covers "loyalty pricing" (FSD text, line
      // 12365) and the till's "Redeem Loyalty Points" action (line 12545).
      // Permission unchanged (`crm.loyalty.read`).
      {
        id: 'party-loyalty',
        labelKey: 'party.nav.loyalty',
        to: '/party/loyalty/programs',
        icon: 'star',
        permission: PARTY_PERMISSIONS.loyaltyRead,
        descriptionKey: 'nav.items.partyLoyalty.description',
      },
    ],
  },
  {
    id: 's-10.2.7',
    kind: 'feature',
    fsd: '10.2.7',
    code: 'POS-F07',
    labelKey: 'nav.fsd.s10_2_7.short',
    fullTitleKey: 'nav.fsd.s10_2_7.full',
    items: [
      {
        id: 'pos-cash-sessions',
        labelKey: 'pos.routeTitles.cashSessions',
        to: '/pos/cash-sessions',
        icon: 'scale',
        permission: POS_PERMISSIONS.sessionRead,
      },
    ],
  },
  {
    id: 's-10.2.8',
    kind: 'feature',
    fsd: '10.2.8',
    code: 'POS-F08',
    labelKey: 'nav.fsd.s10_2_8.short',
    fullTitleKey: 'nav.fsd.s10_2_8.full',
    items: [
      {
        id: 'pos-day-close-board',
        labelKey: 'pos.routeTitles.dayCloseBoard',
        to: '/pos/day-close',
        icon: 'calendar',
        permission: POS_PERMISSIONS.dayCloseRead,
      },
      // Moved in from Part 7 by C17: recipe-driven depletion of stock by POS
      // sales, and the theoretical-vs-actual usage variance it produces.
      // Routes and permissions stay inventory's.
      {
        id: 'inventory-depletions',
        labelKey: 'inventory.routeTitles.depletions',
        to: '/inventory/depletions',
        icon: 'history',
        permission: INVENTORY_PERMISSIONS.recipeRead,
      },
      {
        id: 'inventory-usage-variance',
        labelKey: 'inventory.routeTitles.usageVariance',
        to: '/inventory/usage-variance',
        icon: 'scale',
        permission: INVENTORY_PERMISSIONS.recipeRead,
      },
    ],
  },
  {
    id: 's-10.2.9',
    kind: 'feature',
    fsd: '10.2.9',
    code: 'POS-F09',
    labelKey: 'nav.fsd.s10_2_9.short',
    fullTitleKey: 'nav.fsd.s10_2_9.full',
    items: [
      {
        id: 'pos-retail-ledgers',
        labelKey: 'pos.routeTitles.retailLedgers',
        to: '/pos/retail-ledgers',
        icon: 'inbox',
        permission: POS_PERMISSIONS.retailManage,
        related: ['10.2.10'],
      },
    ],
  },
]
